import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useCrmAudit } from "@/hooks/use-crm-audit";
import type { DBCustomerMonitoringSource } from "@/lib/db-types";

const onError = (err: Error) => toast.error(`Kunne ikke lagre overvåkning: ${err.message}`);

/**
 * Bryteren på en kilde. Å slå av eller på manuelt setter override=true, så den
 * daglige auto-regelen (crm_sync_monitoring_defaults) ikke endrer den igjen.
 */
export function useToggleMonitoringSource() {
  const qc = useQueryClient();
  const audit = useCrmAudit();
  return useMutation({
    mutationFn: async (vars: { row: DBCustomerMonitoringSource; isActive: boolean }) => {
      const { data, error } = await supabase
        .from("customer_monitoring_sources")
        .update({ is_active: vars.isActive, override: true })
        .eq("id", vars.row.id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerMonitoringSource;
    },
    onSuccess: async (row, vars) => {
      audit.log({
        eventType: "monitoring_source.updated",
        entityType: "customer_monitoring_sources",
        entityId: row.customer_id,
        payload: {
          customer_id: row.customer_id,
          source: row.source,
          before: { is_active: vars.row.is_active, override: vars.row.override },
          after: { is_active: row.is_active, override: row.override },
        },
      });
      await invalidateFor(qc, "customer_monitoring_sources", { customerId: row.customer_id });
    },
    onError,
  });
}

/**
 * «Tilbakestill til automatisk»: override av, og kilden følger kundestatus
 * med en gang (RPC reset_monitoring_override), ikke først til natta.
 */
export function useResetMonitoringOverride() {
  const qc = useQueryClient();
  const audit = useCrmAudit();
  return useMutation({
    mutationFn: async (row: DBCustomerMonitoringSource) => {
      const { data, error } = await supabase.rpc("reset_monitoring_override", {
        p_customer_id: row.customer_id,
        p_source: row.source,
      });
      if (error) throw error;
      return { before: row, after: data as DBCustomerMonitoringSource };
    },
    onSuccess: async ({ before, after }) => {
      audit.log({
        eventType: "monitoring_source.updated",
        entityType: "customer_monitoring_sources",
        entityId: after.customer_id,
        payload: {
          customer_id: after.customer_id,
          source: after.source,
          before: { is_active: before.is_active, override: before.override },
          after: { is_active: after.is_active, override: after.override },
        },
      });
      await invalidateFor(qc, "customer_monitoring_sources", { customerId: after.customer_id });
    },
    onError,
  });
}

/**
 * «Bekreft» på en adresse en agent har foreslått: den blir rådgiverens egen og
 * kilden følger kundestatus (RPC confirm_monitoring_identifier).
 */
export function useConfirmMonitoringIdentifier() {
  const qc = useQueryClient();
  const audit = useCrmAudit();
  return useMutation({
    mutationFn: async (row: DBCustomerMonitoringSource) => {
      const { data, error } = await supabase.rpc("confirm_monitoring_identifier", {
        p_customer_id: row.customer_id,
        p_source: row.source,
      });
      if (error) throw error;
      return { before: row, after: data as DBCustomerMonitoringSource };
    },
    onSuccess: async ({ before, after }) => {
      audit.log({
        eventType: "monitoring_source.updated",
        entityType: "customer_monitoring_sources",
        entityId: after.customer_id,
        payload: {
          customer_id: after.customer_id,
          source: after.source,
          before: { is_active: before.is_active, identifier_source: before.identifier_source },
          after: { is_active: after.is_active, identifier_source: after.identifier_source },
        },
      });
      await invalidateFor(qc, "customer_monitoring_sources", { customerId: after.customer_id });
    },
    onError,
  });
}
